import { createContext, useState, useContext, useEffect } from 'react';
import gsap from 'gsap';

const GamificationContext = createContext();

export const useGamification = () => {
    const context = useContext(GamificationContext);
    if (!context) {
        throw new Error('useGamification must be used within GamificationProvider');
    }
    return context;
};

export const GamificationProvider = ({ children }) => {
    const [points, setPoints] = useState(() => {
        const saved = localStorage.getItem('unbox_points');
        return saved ? parseInt(saved) : 0;
    });

    const [level, setLevel] = useState('Bronze');
    const [badges, setBadges] = useState(() => {
        const saved = localStorage.getItem('unbox_badges');
        try {
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            console.error('Failed to parse badges', e);
            return [];
        }
    });

    const levels = {
        Bronze: { min: 0, max: 100, color: '#CD7F32', icon: '🥉' },
        Argent: { min: 100, max: 500, color: '#C0C0C0', icon: '🥈' },
        Or: { min: 500, max: 2000, color: '#FFD700', icon: '🥇' },
        Platine: { min: 2000, max: Infinity, color: '#E5E4E2', icon: '💎' }
    };

    const badgeDefinitions = {
        first_visit: { name: 'Explorateur', icon: '🗺️', description: 'Première visite' },
        theme_explorer: { name: 'Caméléon', icon: '🎨', description: '3 thèmes explorés' },
        first_cart: { name: 'Premier Panier', icon: '🛒', description: 'Premier ajout au panier' },
        social_sharer: { name: 'Ambassadeur', icon: '📢', description: 'Partage sur les réseaux' },
        vip_client: { name: 'Client VIP', icon: '👑', description: 'Premier achat' },
        first_ai_design: { name: 'Créateur IA', icon: '✨', description: 'Premier design généré par IA' },
    };

    // Persist points to localStorage
    useEffect(() => {
        localStorage.setItem('unbox_points', points.toString());
    }, [points]);

    // Persist badges to localStorage
    useEffect(() => {
        localStorage.setItem('unbox_badges', JSON.stringify(badges));
    }, [badges]);

    const addPoints = (amount, reason) => {
        setPoints(prev => {
            const newPoints = prev + amount;
            checkLevelUp(newPoints);
            showNotification(`+${amount} points`, reason);
            return newPoints;
        });
    };

    const checkLevelUp = (currentPoints) => {
        Object.entries(levels).forEach(([levelName, { min, max }]) => {
            if (currentPoints >= min && currentPoints < max && level !== levelName) {
                setLevel(levelName);
                showLevelUpAnimation(levelName);
            }
        });
    };

    const unlockBadge = (badgeId) => {
        if (!badges.includes(badgeId) && badgeDefinitions[badgeId]) {
            setBadges(prev => [...prev, badgeId]);
            showBadgeAnimation(badgeId);
            addPoints(50, `Badge débloqué: ${badgeDefinitions[badgeId].name}`);
        }
    };

    const showNotification = (title, message) => {
        // Create floating notification
        const notification = document.createElement('div');
        notification.className = 'fixed top-32 right-6 z-[100] glass p-4 rounded-xl border border-[var(--primary)] shadow-[0_0_20px_var(--primary-dim)]';
        notification.innerHTML = `
      <div class="flex items-center gap-3">
        <div class="text-2xl">⭐</div>
        <div>
          <div class="font-bold text-[var(--primary)]">${title}</div>
          <div class="text-sm text-gray-400">${message}</div>
        </div>
      </div>
    `;
        document.body.appendChild(notification);

        gsap.fromTo(notification,
            { x: 400, opacity: 0 },
            {
                x: 0,
                opacity: 1,
                duration: 0.5,
                ease: 'back.out(1.7)',
                onComplete: () => {
                    setTimeout(() => {
                        gsap.to(notification, {
                            x: 400,
                            opacity: 0,
                            duration: 0.3,
                            onComplete: () => notification.remove()
                        });
                    }, 3000);
                }
            }
        );
    };

    const showBadgeAnimation = (badgeId) => {
        const badge = badgeDefinitions[badgeId];
        if (!badge) return;

        const badgeEl = document.createElement('div');
        badgeEl.className = 'fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm';
        badgeEl.innerHTML = `
      <div class="glass p-12 rounded-3xl border border-[var(--primary)] text-center max-w-md">
        <div class="text-8xl mb-6 animate-bounce">${badge.icon}</div>
        <h3 class="text-3xl font-bold text-white mb-2">Badge Débloqué !</h3>
        <p class="text-xl text-[var(--primary)] font-bold mb-2">${badge.name}</p>
        <p class="text-gray-400">${badge.description}</p>
        <button class="mt-6 btn-glow px-8 py-3 rounded-lg" onclick="this.parentElement.parentElement.remove()">
          Continuer
        </button>
      </div>
    `;
        document.body.appendChild(badgeEl);

        gsap.fromTo(badgeEl.firstElementChild,
            { scale: 0, rotation: -180 },
            { scale: 1, rotation: 0, duration: 0.6, ease: 'back.out(1.7)' }
        );
    };

    const showLevelUpAnimation = (newLevel) => {
        const levelData = levels[newLevel];
        showNotification(`🎉 Niveau ${newLevel} !`, `Vous avez atteint le niveau ${levelData.icon}`);
    };

    return (
        <GamificationContext.Provider value={{
            points,
            level,
            badges,
            levels,
            badgeDefinitions,
            addPoints,
            unlockBadge
        }}>
            {children}
        </GamificationContext.Provider>
    );
};
